/*
 * Challenge questions (audit Phase 3.3). Replaces
 * wwwroot/js/ChallengeQuestion/List.js and _namespace.js on the Phase 3.1
 * core. No jQuery.
 *
 * One screen: the questions offered to People during the password-reset
 * challenge, for the Domain the rail is pointed at plus the global catalogue.
 * A question is created, renamed or deleted; rescoping is not offered because
 * the Api has no PUT for it.
 *
 * The modal placeholder is INSIDE the page the list is rendered into, so every
 * write closes the dialog before redrawing -- see hideModalThen.
 */
import { ajaxJson, ajaxModal, ajaxSwap, failToast, summaryOf } from '../core/ajax';
import { one } from '../core/dom';
import { serialize } from '../core/forms';
import { confirm, hideModalThen } from '../core/modal';
import { toast } from '../core/toast';
import { isValid, setValidationModelState } from '../core/validation';

const MODAL = 'ChallengeQuestionModal';
const CREATE_FORM = 'ChallengeQuestionCreateForm';
const EDIT_FORM = 'ChallengeQuestionEditForm';
const CREATE_ALERT = 'ChallengeQuestionCreateMessageContainer';
const EDIT_ALERT = 'ChallengeQuestionEditMessageContainer';

/** The Domain the rail is pointed at; 0 is the global catalogue. */
function currentDomainId(): string {
    return (one('#CurrentDomainId') as HTMLInputElement | null)?.value || '0';
}

/**
 * The save button's icon swap while a write is in flight. Scoped to the modal
 * rather than to the form: both footers sit OUTSIDE their `<form>`, so the
 * legacy `$('#…Form .fa-save')` matched nothing and no spinner ever appeared.
 */
function saving(working: boolean): void {
    const modal = one(`#${MODAL}`);
    if (!modal) return;
    for (const node of Array.from(modal.querySelectorAll('.fa-save'))) node.classList.toggle('d-none', working);
    for (const node of Array.from(modal.querySelectorAll('.fa-spinner'))) node.classList.toggle('d-none', !working);
}

export const List = {
    ShowDetail(questionId: number | string): void {
        void ajaxModal({
            modal: MODAL,
            url: `ChallengeQuestion/Detail/${questionId}`,
            title: null,
            // Naming the form is what attaches validation to it. Without this
            // the edit form was never parsed, so isValid() built a validator
            // with no rules and answered true for everything -- an emptied
            // question reached the Api as a 400 instead of stopping at the
            // field.
            formId: EDIT_FORM,
            focusId: 'QuestionText',
            errorTitle: `Question ${questionId}`,
            errorMessage: 'Question Detail failed to execute:',
        });
    },

    NewQuestion(): void {
        void ajaxModal({
            modal: MODAL,
            url: `ChallengeQuestion/Create/${currentDomainId()}`,
            formId: CREATE_FORM,
            focusId: 'QuestionText',
            errorTitle: 'New Challenge Question',
            errorMessage: 'Failed to render new question form',
        });
    },

    NewQuestionSave(): void {
        const form = one(`#${CREATE_FORM}`) as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        saving(true);
        void ajaxJson({
            url: 'ChallengeQuestion',
            method: 'POST',
            data: serialize(form),
            alertInto: CREATE_ALERT,
            errorTitle: 'Failed to create question',
            onSuccess: () => {
                toast('success', 'Question created');
                hideModalThen(MODAL, () => void List.Reload());
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                // The form, not the alert div beside it: the model-level ('')
                // entry is rendered into the container's OWN
                // div.validation-summary, and only the form has one.
                if (modelState) setValidationModelState(modelState, `#${CREATE_FORM}`);
                toast('danger', 'Failed to create question', summaryOf(data));
                return true;
            },
            onComplete: () => saving(false),
        });
    },

    SaveDetail(questionId: number | string): void {
        const form = one(`#${EDIT_FORM}`) as HTMLFormElement | null;
        if (!form || !isValid(form)) return;
        saving(true);
        void ajaxJson({
            url: `ChallengeQuestion/${questionId}`,
            method: 'PUT',
            data: serialize(form),
            alertInto: EDIT_ALERT,
            errorTitle: 'Failed to save question',
            onSuccess: () => {
                toast('success', 'Question saved');
                hideModalThen(MODAL, () => void List.Reload());
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                if (modelState) setValidationModelState(modelState, `#${EDIT_FORM}`);
                toast('danger', 'Failed to save question', summaryOf(data));
                return true;
            },
            onComplete: () => saving(false),
        });
    },

    DeleteQuestion(questionId: number | string): void {
        void confirm({
            title: 'Delete Challenge Question',
            html: 'Delete this challenge question?<br/><br/>'
                + '<span class="text-danger">Any Person answers already given to it are removed, '
                + 'so anyone who relied on it has one fewer way to pass the reset challenge.</span>',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `ChallengeQuestion/${questionId}`,
                    method: 'DELETE',
                    alertInto: EDIT_ALERT,
                    errorTitle: 'Failed to delete question',
                    onSuccess: () => {
                        toast('success', 'Question deleted');
                        hideModalThen(MODAL, () => void List.Reload());
                    },
                    onFailure: failToast('Failed to delete question'),
                });
            },
        });
    },

    Reload(): Promise<void> {
        return ajaxSwap({
            container: 'ViewContainer',
            url: `ChallengeQuestion/Domain/${currentDomainId()}`,
            alertTitle: 'Failed to reload questions',
        });
    },
};
